import type { Option, Screen } from '../types';
import type { WorkflowOptionValue } from './workflowUi';
import { createRichTooltipContent } from '../ui/common/RichTooltipContent';

const tooltip = (
  title: string,
  summary: string,
  why?: string,
  downstream?: string,
  note?: string
) => createRichTooltipContent({ title, summary, why, downstream, note });

export const workflowSetupTooltips = {
  selectedWorkflow: tooltip(
    'Selected workflow',
    'Review the selected engineering deliverable before changing the Revit source mappings. If needed, you can change the workflow using the "Change workflow" button.',
    'This selection ensures that the required setup matches the expected output.',
    'It determines which setup steps are shown and which Revit source fields PipeEX requires later in the workflow.'
  ),
  workflowSelection: tooltip(
    'Choose workflow',
    'Choose the engineering output this Revit model must support.',
    'Each workflow requires a different set of source parameters and mappings.',
    'Changing the workflow clears later setup progress so connector, bolt, category, and property mappings match the new output.',
    'Choose ISO drawings for PCF-backed drawings, Pressure drop for calculation-ready data, or Stress analysis for stress-ready metadata.'
  ),
  changeWorkflow: tooltip(
    'Change workflow',
    'Choose a different engineering output after setup has started.',
    'PipeEX keeps the shared parameter file and reusable defaults, then clears later progress to prevent mismatched mappings.',
    'Setup resumes at Connectors with the steps required for the new output.'
  ),
  objectType: tooltip(
    'OBJECT-TYPE mapping',
    'Choose the parameter that defines each component\'s PCF object type, such as PIPE, ELBOW, or VALVE.',
    'This is the primary component classification used by the workflow. Subsequent mapping rules rely on this value being correct.',
    'If this value is incorrect, PipeEX may apply object-specific attributes and SKEY rules intended for a different component type.'
  ),
  sKey: tooltip(
    'SKEY mapping',
    'Choose the parameter that stores the symbol key (SKEY) used to represent the component in PCF-based applications.',
    'This field links the component classification selected earlier to the symbol used in the exported PCF file.',
    'If the SKEY is incorrect, the component type may be correct, but the wrong symbol may appear in the generated isometric drawing.'
  ),
  pipingSpec: tooltip(
    'PIPING-SPEC mapping',
    'Choose the parameter that stores the piping specification code used for isometric and BOM workflows.',
    'Subsequent connector and export mapping relies on a consistent piping specification value.',
    'PipeEX includes this value in the exported PCF data and uses it in subsequent filtering rules.'
  ),
  systemGrouping: tooltip(
    'System grouping rule',
    'Choose BySystemName to group piping systems by Revit system name, or BySystemTypeName to group them by Revit system type name.',
    'The selected value is stored in the profile and determines how PipeEX creates PCF pipeline groups.',
    'Grouping too broadly can combine unrelated lines. Grouping too narrowly can split lines that should remain together.',
    'Use BySystemName when system names are maintained consistently. Use BySystemTypeName when the model is managed primarily by system type.'
  ),
  sharedParameterFileSection: tooltip(
    'Shared parameter file',
    'Choose the shared parameter file PipeEX should use to find the required parameter definitions.',
    'PipeEX uses this file to populate parameter selection lists and create the instance parameters required for connector setup.',
    'If a required parameter definition is missing from the selected file, PipeEX cannot create the corresponding parameter.'
  ),
  targetCategoriesSection: tooltip(
    'Revit target categories',
    'Review which Revit categories can receive the required parameters mapped above.',
    'This summary verifies the category scope before connector-specific creation rules are configured.',
    'Required pipe mappings target pipe categories. OBJECT-TYPE can also target Generic Models and Mechanical Equipment.'
  ),
} as const;

export const flowPreviewTooltips = {
  workflowSetup: tooltip(
    'Configure workflow',
    'Choose the engineering output and map its required PCF defaults before connector setup.',
    'Starting with the deliverable prevents unrelated fields from entering the mapping profile.',
    'The selection determines the remaining setup steps and required source mappings.'
  ),
  connectorBinding: tooltip(
    'Map connectors',
    'Map connector endpoints, material, rating, description, and component fields to maintained Revit parameters.',
    'These source mappings define the component identity reused by PCF mapping and validation.',
    'Missing mappings produce incomplete END-POINT or BOM data in downstream PCF output.'
  ),
  quickFillWorkspace: tooltip(
    'Complete family data',
    'Use Quick Fill to review and populate family values in bulk before writing them to Revit.',
    'Comparing families and fields together exposes gaps that are difficult to find one family at a time.',
    'Saved values become the source data read by mapping, validation, and PCF export.'
  ),
  finalMappingWorkspace: tooltip(
    'Finalize mapping',
    'Review each Revit source-to-PCF rule, resolve required fields, and save the export profile.',
    'This converts the guided setup choices into a durable engineering configuration.',
    'The saved profile drives validation and PCF output for the selected workflow.'
  ),
} as const;

export const workflowFeatureTooltips: Record<WorkflowOptionValue, ReturnType<typeof tooltip>> = {
  'Iso Drawings Generation': tooltip(
    'ISO drawings',
    'Choose this workflow when PCF output will drive ISO drawing production.',
    'It includes connector rules, ISO-specific categories, and export-ready mappings.',
    'The resulting profile supplies the category tags and component data used to generate ISO deliverables.'
  ),
  'Pressure Drop Calculations': tooltip(
    'Pressure drop',
    'Choose this workflow to prepare Revit piping data for pressure drop calculations.',
    'It prioritizes system grouping, connector defaults, and analysis parameters instead of ISO-only fields.',
    'The resulting mappings provide consistent source data to downstream calculation tools.'
  ),
  'Stress Calculations': tooltip(
    'Stress analysis',
    'Choose this workflow to prepare Revit component data for stress calculations.',
    'It prioritizes component identity and consistent Quick Fill values without ISO-only fields.',
    'The saved mapping provides validated source data to downstream stress tools.'
  ),
};

export const connectorBindingTooltips = {
  setupPanel: tooltip(
    'Connector setup scope',
    'In this setup step, first choose where PipeEX creates and stores connector parameters and whether their values are defined by TYPE or INSTANCE. Then map the Revit parameters used for PCF connector, BOM, and component data.',
    'These settings determine whether connector parameters are stored in the Revit project or in editable families, and whether one value is shared by a type or stored separately for each instance. The mappings define which Revit parameters supply values for PCF connection end types, BOM output, component descriptions, insulation, and tracing data.',
    'These settings control where and how connector parameters are created and stored. The Connector, BOM, and Component mappings are used by PipeEX during validation and to populate the PCF output. Accurate mappings help reduce manual cleanup in downstream workflows.'
  ),
  parameterMode: tooltip(
    'Connector parameter creation mode',
    'Choose whether connector parameters are stored in the project or in families. To store connector parameters in the project, select Project Type or Project Instance. To store connector parameters in families, select Family Parameters (advanced).',
    'This setting determines how and where PipeEX creates the mapped connector parameters.',
    'When Project Type is selected, one value is stored per type. When Project Instance is selected, a separate value is stored for each placed element. When Family Parameters (advanced) is selected, pipes continue to use project type parameters, while editable family categories use family type or family instance parameters according to the selected mode.'
  ),
  pipeException: tooltip(
    'Connector mode behavior',
    'Project modes create category-bound project parameters. Family modes add or convert family parameters on matching editable families, but pipes stay on project type parameters.',
    'If a parameter already exists with the wrong type or instance scope, PipeEX updates it to match the selected connector mode when Revit allows the conversion.',
    'This keeps pipe types valid for system-family behavior while still applying direct family parameters to fittings, accessories, generic models, and mechanical equipment.'
  ),
  setupApplied: tooltip(
    'Connector setup applied',
    'The connector parameter setup has already been applied for the active project.',
    'This keeps the wizard from repeating the same Revit operation while still showing that the setup is reversible before you continue.',
    'Continuing uses the existing setup state and moves into the next workflow step.'
  ),
  revertSetup: tooltip(
    'Revert connector setup',
    'Undo the latest connector parameter setup operation for the active project when you need to revise the creation scope.',
    'Use this before continuing if the selected project/family or type/instance mode was wrong.',
    'Reverting affects the parameter setup operation, not the mapped dropdown choices in this page.'
  ),
  connectorsSection: tooltip(
    'Connector endpoint mappings',
    'Map each PCF endpoint slot to the Revit parameter that stores its endpoint type.',
    'Revit connector geometry defines the physical connection points, while these mapped values define how each connection is described in the PCF.',
    'Validation and export use these mappings to check connector completeness and write END-POINT data.'
  ),
  bomSection: tooltip(
    'Connector BOM mappings',
    'Select the material, design standard, and pressure class parameters used to identify components in the BOM.',
    'These values help distinguish visually similar components that should not be treated as interchangeable.',
    'PipeEX uses these mappings during validation and includes the selected values as component metadata in the BOM output.'
  ),
  componentsSection: tooltip(
    'Connector component mappings',
    'Select the description, insulation, and tracing parameters used to provide additional component information in the PCF output.',
    'These optional values provide information beyond connector size and end-type data and can reduce manual cleanup in downstream workflows.',
    'PipeEX uses these mappings to populate component attributes and related metadata in the PCF output.'
  ),
  endpoint1: tooltip(
    'Primary connector end type',
    'Choose the parameter that stores the end-type value for the family’s primary connector.',
    'This tells PipeEX which value represents the primary connector end type before final mapping.',
    'PipeEX writes this value to END-POINT_1 in the PCF output and uses it in subsequent connector mapping steps.'
  ),
  endpoint2: tooltip(
    'Secondary connector end type',
    'Choose the parameter that stores the end-type value for the family’s secondary connector, typically on the opposite side of the run or on the first branch.',
    'For two-port families, both connector end types must be mapped so PipeEX can correctly describe the connection pair.',
    'Missing or inconsistent values can cause errors or ambiguity during validation and PCF export.'
  ),
  endpoint3: tooltip(
    'Third connector end type',
    'Use this only for families with a third connector, such as the branch connector on a tee or specialty fitting.',
    'This field is optional because many families do not require a third connector mapping.',
    'When mapped, PipeEX can preserve the third connection in the PCF output instead of treating the component as a two-port fitting.'
  ),
  endpoint4: tooltip(
    'Additional connector end types (4+)',
    'Use this field for families with four or more connectors.',
    'This mapping is used for complex fittings or equipment with additional connections beyond the first three.',
    'Leave it empty unless the family has additional connector end-type values that must be preserved in the PCF export.'
  ),
  material: tooltip(
    'Material source',
    'Choose the parameter that stores the material value used for fabrication and BOM output.',
    'This mapping defines the material value used throughout the BOM and subsequent mapping steps.',
    'PipeEX uses this value during validation and includes it in the BOM output to distinguish otherwise similar components.'
  ),
  designStd: tooltip(
    'Design standard source',
    'Choose the parameter that stores the applicable design standard or code for the component.',
    'This mapping ensures standards such as ASME, DIN, or ISO are included in the exported data.',
    'PipeEX uses this value to distinguish components by design standard in BOM output and downstream workflows.'
  ),
  pressureClass: tooltip(
    'Pressure class source',
    'Choose the parameter that stores the pressure class or rating for flanges, fittings, and other pressure-rated components.',
    'The value of this parameter helps distinguish visually similar components with different pressure classes or ratings.',
    'PipeEX uses this parameter value in BOM output and downstream workflows to ensure components with different pressure ratings are treated separately.'
  ),
  insulationSpec: tooltip(
    'Insulation specification source',
    'Choose the parameter that stores the insulation specification. Use Revit’s built-in insulation type when it is used consistently in the model; otherwise, select the corresponding shared parameter.',
    'This mapping tells PipeEX which value to use for insulation data in the PCF output.',
    'Using a consistent source helps prevent conflicting or duplicate insulation data in downstream workflows.'
  ),
  insulationThickness: tooltip(
    'Insulation thickness source',
    'Choose the parameter that stores the insulation thickness. Use Revit’s built-in insulation thickness when it is available and used consistently in the model; otherwise, select the corresponding shared parameter.',
    'This mapping tells PipeEX which value to use for insulation thickness in the PCF output.',
    'Using a consistent source helps ensure insulation thickness values remain accurate in downstream workflows.'
  ),
  tracingSpec: tooltip(
    'Tracing specification source',
    'Choose the parameter that stores the heat tracing specification for the line or component.',
    'This mapping tells PipeEX which value to use for tracing data in the PCF output.',
    'PipeEX uses this value in relevant process properties and downstream workflows, reducing the need for manual corrections.'
  ),
  itemDescription: tooltip(
    'Item description source',
    'Choose the parameter that stores the item description used for fabrication, procurement, or BOM output.',
    'This mapping tells PipeEX which value to use as the default component description before the final mapping step.',
    'This value is used as the component description in downstream deliverables.'
  ),
} as const;

export const boltOptionTooltips = {
  modelGasketBased: tooltip(
    'Derive BOLT from GASKET',
    'Derive standalone BOLT elements from qualifying GASKET elements.',
    'The wizard requires the gasket-sourced bolt fields and activation parameter used by this path.',
    'Final mapping places the derived bolt attributes on BOLT.'
  ),
  modelBoltBased: tooltip(
    'Use source BOLT data',
    'Use bolt-based source data that already represents BOLT elements.',
    'PipeEX does not derive BOLT elements from GASKET in this mode.',
    'Final mapping maps the available source fields to BOLT.'
  ),
  baseFamilyBoltBased: tooltip(
    'Bolt-based family strategy',
    'Use bolt-based source BOLT data as-is.',
    'The gasket-derived BOLT step does not run in BoltBased mode.',
    'Later mapping surfaces the available source data on BOLT.'
  ),
  baseFamilyGasketBased: tooltip(
    'Gasket-based family strategy',
    'Derive standalone BOLT elements from qualifying GASKET elements.',
    'The activation parameter decides which GASKET elements produce BOLT data.',
    'Later mapping surfaces the derived data on BOLT.'
  ),
} as const;

export const boltConnectionTooltips = {
  setupPanel: tooltip(
    'Bolt setup scope',
    'Choose where PipeEX creates bolt parameters for the selected PCF bolt strategy.',
    'The required setup depends on whether PipeEX derives BOLT data from GASKET or uses existing BOLT data from the model.',
    'The selected scope determines where bolt parameters are created and which Revit categories or families are checked. The bolt strategy determines which mappings are required.'
  ),
  boltModelingChoice: tooltip(
    'Bolt export strategy',
    'Choose whether PipeEX derives standalone BOLT elements from qualifying GASKET elements or uses source BOLT data.',
    'This is the main logic switch for the entire step and decides which remaining fields are required.',
    'The selection changes both the validation rules in this step and the target object type in final mapping.'
  ),
  switchModelingChoice: tooltip(
    'Switch bolt strategy',
    'Switch between deriving BOLT data from GASKET and using existing BOLT data from the model.',
    'Use this when the project requires a different PCF bolt strategy from the one selected earlier.',
    'Switching the strategy changes which mappings are required on this page and how PipeEX generates BOLT data in the final PCF output.'
  ),
  parameterMode: tooltip(
    'Bolt parameter mode',
    'Choose project or family storage, then choose TYPE for one value per type or INSTANCE for one value per placed element.',
    'Project mode binds the selected categories and loads the matching reference family when the target is missing.',
    'Family mode can load or update the matching reference family when bolt setup needs direct family parameters.'
  ),
  targetBehavior: tooltip(
    'Bolt parameter target',
    'Project modes create and bind bolt parameters to the selected Revit categories. Family modes create the parameters in the corresponding editable families.',
    'This setting determines where PipeEX creates the bolt parameters and which Revit categories or families receive them.',
    'If a required family is not available in the project, PipeEX loads the corresponding reference family before creating the bolt parameters.'
  ),
  gasketTargetBehavior: tooltip(
    'Gasket parameter target',
    'Project modes create and bind gasket parameters to the selected Revit categories. Family modes create the parameters in the corresponding editable families.',
    'This setting determines where PipeEX creates the gasket parameters and which Revit categories or families receive them.',
    'If a required family is not available in the project, PipeEX loads the corresponding reference family before creating the gasket parameters.'
  ),
  projectCategories: tooltip(
    'Project categories',
    'Choose the Revit categories that will receive bolt parameters when a Project parameter mode is selected.',
    'These categories determine where PipeEX creates and binds the bolt parameters in the project.',
    'At least one category must remain selected so the bolt parameters always have a valid target.'
  ),
  setupApplied: tooltip(
    'Bolt setup applied',
    'The bolt parameter setup has already been applied for the active project.',
    'This prevents duplicate setup work while keeping the previous operation visible and reversible.',
    'Continuing syncs the bolt selections into final mapping without rerunning setup.'
  ),
  revertSetup: tooltip(
    'Revert bolt setup',
    'Undo the latest bolt parameter setup operation for the active project.',
    'Use this when the bolt strategy, parameter scope, or target categories need to be changed before continuing.',
    'Reverting setup does not clear the bolt field mappings already selected on this page.'
  ),
  coreSection: tooltip(
    'Core bolt mappings',
    'Review the bolt dimension and gasket mode fields required for the selected export workflow.',
    'These mappings provide the minimum data PipeEX needs to define bolt hardware directly instead of deriving it from model geometry.',
    'PipeEX uses these values in the final mapping and BOM output for BOLT elements.'
  ),
  supportingSection: tooltip(
    'Supporting bolt settings',
    'Review the bolt family mode and any optional field used to determine whether bolt data is included in the PCF output.',
    'These settings ensure the bolt configuration matches the selected parameter and modeling strategy.',
    'PipeEX uses these settings during final mapping to apply bolt attributes to the correct components.'
  ),
  mappingToggle: tooltip(
    'Final mapping bolt toggle',
    'Enable or disable bolt modeling inside the final mapping workspace when the profile needs a local correction.',
    'This mirrors the earlier bolt setup decision without changing Revit parameter creation by itself.',
    'When enabled, the bolt mapping fields below decide which parameters feed bolt output.'
  ),
  disabledMappingEcho: tooltip(
    'Bolt mapping disabled',
    'Bolt modeling is off for this mapping profile, so bolt parameter fields are hidden.',
    'Use this state when bolt data should remain outside the saved PCF mapping profile.',
    'Enable bolt modeling if the profile must map or validate BOLT attributes.'
  ),
  boltBaseFamily: tooltip(
    'Derived bolt family mode',
    'This field reflects the internal family mode PipeEX will use after you choose the export strategy above.',
    'It is shown here so you can confirm the wizard translated the modeling decision correctly.',
    'Later mapping and sync actions use this value to place bolt attributes on the correct object family.'
  ),
  boltDia: tooltip(
    'Bolt diameter parameter',
    'Choose the parameter that stores the nominal bolt diameter for the connection.',
    'Bolt diameter is one of the key values PipeEX needs to define the bolt correctly.',
    'PipeEX uses this value in BOM and procurement output where the bolt size must be specified explicitly.'
  ),
  boltQuantity: tooltip(
    'Bolt quantity parameter',
    'Choose the parameter that stores the number of bolts required for the connection or bolt set.',
    'This mapping ensures PipeEX reads the bolt quantity from the model instead of deriving it from flange standards.',
    'PipeEX uses this value in BOM and procurement output, where an accurate bolt count is required.'
  ),
  boltLength: tooltip(
    'Bolt length parameter',
    'Choose the parameter that stores the selected bolt length for the connection.',
    'Bolt length is one of the key dimensions PipeEX needs to define the bolt correctly.',
    'PipeEX uses this value in BOM and procurement output so the bolt length does not need to be inferred.'
  ),
  boltSetActive: tooltip(
    'BOLT-SET-ACTIVE parameter',
    'For GasketBased, choose the yes/no parameter that marks which GASKET elements should produce a BOLT element.',
    'PipeEX reads this field only when deriving BOLT elements from GASKET.',
    'Inactive GASKET elements do not produce BOLT elements.'
  ),
  itemDescription: tooltip(
    'Bolt description source',
    'For gasket-based workflows, choose the parameter that stores the bolt description used in the exported BOM.',
    'This mapping ensures the bolt description matches the terminology used by procurement and fabrication teams.',
    'PipeEX uses this value as the bolt description in BOM and other exported data.'
  ),
  material: tooltip(
    'Bolt material source',
    'For gasket-based workflows, choose the parameter that stores the bolt material or grade.',
    'This mapping tells PipeEX which material value to use when bolt data is not modeled as a separate family.',
    'PipeEX uses this value in BOM output and validation to distinguish compatible and incompatible bolt selections.'
  ),
  standard: tooltip(
    'Bolt standard source',
    'For gasket-based workflows, choose the parameter that stores the standard or code used for the bolt geometry and thread form.',
    'This mapping helps identify the correct bolt hardware when size and quantity alone are not sufficient.',
    'PipeEX uses this value in BOM output and downstream documentation to provide the standard reference needed for procurement.'
  ),
} as const;

export const additionalCategoryTooltips = {
  setupPanel: tooltip(
    'Additional-category setup scope',
    'Choose where PipeEX creates valve, support, and instrument identifier parameters for ISO workflows.',
    'These parameters are used as references in isometric drawings, so they should be created according to how the corresponding Revit categories or families are managed.',
    'This setting controls only where the parameters are created. The selected mappings determine which identifier values are included in the PCF output.'
  ),
  parameterMode: tooltip(
    'Additional-category parameter mode',
    'Choose whether the parameters are stored in the Revit project or in editable families. Then choose TYPE to share one value across all instances of the same type, or INSTANCE to store a separate value for each placed element.',
    'Project mode creates and binds the parameters to the selected Revit categories. Family mode creates them in the corresponding editable VALVE, SUPPORT, and INSTRUMENT families.',
    'This setting ensures the identifier parameters are created in the Revit categories or families that use them for ISO output.'
  ),
  targetBehavior: tooltip(
    'Additional-category target behavior',
    'Project modes apply to the selected Revit categories. Family modes apply to matching editable component families.',
    'This notice shows the concrete target set for the setup operation before Revit is changed.',
    'Use it to confirm the tag parameters will land where reviewers expect to maintain them.'
  ),
  projectCategories: tooltip(
    'Project categories',
    'Choose the Revit categories that will receive valve, support, and instrument identifier parameters.',
    'These categories determine where PipeEX creates and binds the identifier parameters used for ISO workflows.',
    'At least one category must remain selected so the identifier parameters always have a valid target.'
  ),
  setupApplied: tooltip(
    'Additional-category setup applied',
    'The additional-category parameter setup has already been applied for the active project.',
    'This keeps the wizard from repeating the same setup operation while preserving the option to revert it.',
    'Continuing syncs the tag choices into final mapping and moves forward.'
  ),
  revertSetup: tooltip(
    'Revert additional-category setup',
    'Undo the latest additional-category parameter setup operation for the active project.',
    'Use this before continuing if the tag parameters were created with the wrong project/family or type/instance scope.',
    'Reverting setup does not clear the selected valve, support, or instrument mappings.'
  ),
  valveSection: tooltip(
    'Valve identifier mappings',
    'Choose the Revit parameter that stores the valve identifier used in ISO output and drawing review.',
    'Valve identifiers are references, not descriptions, so they should use the maintained valve tag or identifier parameter.',
    'PipeEX uses this value for VALVE object attributes in the final mapping and PCF output.'
  ),
  supportsSection: tooltip(
    'Support identifier mappings',
    'Choose the Revit parameters that store the support identifier and optional support standard reference used in ISO output.',
    'Support identifiers and standard references are often checked against detail drawings or catalogs, so they should be mapped separately.',
    'PipeEX uses these values for SUPPORT object attributes in the final mapping and PCF output.'
  ),
  instrumentSection: tooltip(
    'Instrument identifier mappings',
    'Choose the Revit parameter that stores the instrument identifier used in ISO output and drawing references.',
    'Instrument identifiers should match the project or P&ID naming convention rather than generic Revit family descriptions.',
    'PipeEX uses this value for INSTRUMENT object attributes in the final mapping and PCF output.'
  ),
  isoOnlyNotice: tooltip(
    'ISO-only category mapping',
    'Additional categories are mainly used by ISO drawing workflows.',
    'Other workflows can keep this section visible for review, but they normally do not need these tag outputs.',
    'Switching workflow context can change whether these mappings are required downstream.'
  ),
  valveTag: tooltip(
    'Valve tag source',
    'Choose the parameter that stores the valve identifier used in drawings and review workflows.',
    'This mapping tells PipeEX which value to use as the valve reference in ISO output.',
    'PipeEX uses this value to identify the valve consistently across PCF output, drawings, and P&IDs.'
  ),
  supportsTag: tooltip(
    'Support tag source',
    'Choose the parameter that carries the support mark or identifier, not a generic description field.',
    'Supports need a stable visible reference if they will appear in ISO annotations or fabrication review.',
    'That tag becomes the readable support identifier carried into the exported support object.'
  ),
  supportsSsref: tooltip(
    'Support specification reference',
    'Choose the parameter that points to the standard support sheet, detail, or catalog reference when your team uses one.',
    'This field is optional because not every project manages supports through a standard reference library.',
    'If mapped, the export can carry a reusable detail reference instead of only a support tag.'
  ),
  instrumentTag: tooltip(
    'Instrument tag source',
    'Choose the parameter that already acts as the instrument identifier across the project.',
    'This keeps instrument references aligned with the same tag used in drawings, controls, and P&ID review.',
    'The selected value becomes the export-side identifier for instrument objects.'
  ),
} as const;

const processField = (
  title: string,
  summary: string,
  downstream?: string,
  note?: string
) =>
  tooltip(
    title,
    summary,
    'Use this step to bind optional process metadata to the fields PipeEX can carry into export.',
    downstream,
    note
  );

const projectField = (
  title: string,
  summary: string,
  downstream?: string,
  note?: string
) =>
  tooltip(
    title,
    summary,
    'Use this step to bind optional project and title-block metadata to the exported header context.',
    downstream,
    note
  );

export const processPropertiesTooltips = {
  processCondition: tooltip(
    'Process condition source',
    'Choose the parameter that stores the process condition associated with the line.',
    'This mapping tells PipeEX which value to use for optional process condition data in the PCF output.',
    'PipeEX uses this value as the process condition reference in the exported system data.'
  ),
  pipingSpec: tooltip(
    'Process piping spec source',
    'Choose the parameter that stores the piping specification used for process or stress analysis workflows.',
    'This mapping tells PipeEX which value to use for the piping specification in the PCF output.',
    'PipeEX uses this value to provide the specification reference used for classification, filtering, and downstream rules.'
  ),
  tracingSpec: tooltip(
    'Process tracing spec source',
    'Choose the parameter that stores the heat tracing specification for the line.',
    'This mapping tells PipeEX which value to use for process tracing data in the PCF output.',
    'PipeEX uses this value as the tracing specification reference in downstream engineering and documentation workflows.'
  ),
  attribute12: tooltip(
    'Design pressure source',
    'Choose the parameter that stores the design pressure for the line. Do not use the normal operating pressure.',
    'This mapping tells PipeEX which value to use as the design pressure in the PCF output.',
    'PipeEX uses this value as the design pressure reference for engineering checks and downstream workflows.'
  ),
  attribute13: tooltip(
    'Design temperature source',
    'Choose the parameter that stores the design temperature for the line. Do not use the normal operating temperature.',
    'This mapping tells PipeEX which value to use as the design temperature in the PCF output.',
    'PipeEX uses this value as the design temperature reference for engineering review and downstream workflows.'
  ),
  attribute14: tooltip(
    'Operating pressure source',
    'Choose the parameter that stores the normal operating pressure for the line.',
    'This mapping tells PipeEX which value to use as the operating pressure in the PCF output.',
    'PipeEX uses this value to represent the normal operating condition alongside the design pressure in downstream workflows.'
  ),
  attribute15: tooltip(
    'Operating temperature source',
    'Choose the parameter that stores the normal operating temperature for the line.',
    'This mapping tells PipeEX which value to use as the operating temperature in the PCF output.',
    'PipeEX uses this value to represent the normal operating condition alongside the design temperature in downstream workflows.'
  ),
  attribute16: tooltip(
    'Service description source',
    'Choose the parameter that describes the fluid, medium, or service carried by the line.',
    'This mapping tells PipeEX which value to use as the service description in the PCF output.',
    'PipeEX uses this value to provide a clear description of the line service in downstream workflows.'
  ),
  attribute17: tooltip(
    'PED group source',
    'Choose the parameter that stores the PED fluid group used for regulatory classification.',
    'This mapping tells PipeEX which value to use for PED fluid group data in the PCF output.',
    'PipeEX uses this value to preserve PED classification information for downstream review and compliance workflows.'
  ),
  attribute18: tooltip(
    'PED category source',
    'Choose the parameter that stores the applicable PED equipment category when this classification is maintained in the model.',
    'This mapping tells PipeEX which value to use for the PED category in the PCF output.',
    'PipeEX uses this value to preserve PED classification information for downstream review and compliance workflows.',
    'Leave this field unmapped if the project does not use PED classification.'
  ),
  attribute19: processField(
    'Test pressure field',
    'Choose the parameter used for hydrostatic or pneumatic test pressure when the project tracks test data.',
    'This makes the required test envelope visible in downstream documentation.'
  ),
  attribute20: processField(
    'Test type field',
    'Choose the parameter that identifies the test method, such as hydro or pneumatic.',
    'It clarifies how the associated test pressure should be interpreted downstream.'
  ),
  attribute21: processField(
    'Heat tracing field',
    'Choose the tracing-related parameter if traced-line metadata needs to be carried in process export.',
    'This preserves tracing intent alongside other process conditions.'
  ),
  attribute22: processField(
    'Insulation thickness field',
    'Choose the thickness parameter only if process or documentation workflows need insulation thickness in export.',
    'This carries the quantitative insulation value into the process metadata bundle.'
  ),
  attribute23: processField(
    'Insulation type field',
    'Choose the insulation classification field only if process or documentation workflows need it in export.',
    'This carries the qualitative insulation type alongside thickness or tracing metadata.'
  ),
  projectProperties: projectField(
    'Project property source',
    'Choose the parameter that acts as the main project-associated metadata source when the model has one.',
    'It becomes the anchor point for project-level context carried into the export header.'
  ),
  projectIdentifier: projectField(
    'Project identifier field',
    'Choose the unique project code field used to tie the export back to a specific job or package.',
    'This gives the exported header a stable project reference that other systems can read reliably.'
  ),
  projectName: projectField(
    'Project name field',
    'Choose the human-readable project title that should appear in exported header metadata.',
    'This is the visible project label most downstream reviewers will recognize first.'
  ),
  area: projectField(
    'Area field',
    'Choose the plant area, unit, or zone field if the export needs to preserve location context.',
    'This keeps the header tied to the part of the facility the line belongs to.'
  ),
  attribute1: projectField(
    'Client name field',
    'Choose the client or owner name field used in title blocks or project metadata.',
    'This carries the owning organization into the exported header context.'
  ),
  attribute2: projectField(
    'Project address field',
    'Choose the project-site or facility address field when formal documentation needs it.',
    'This preserves location context for title-block-style exports.'
  ),
  attribute3: projectField(
    'Client department field',
    'Choose the department or division field when the project tracks the client organization in more detail.',
    'This gives downstream documentation a finer-grained organizational reference.'
  ),
  attribute4: projectField(
    'Client project number field',
    'Choose the client-side project number if the export needs to align with the client\'s own tracking scheme.',
    'This helps downstream recipients reconcile the export with their internal numbering.'
  ),
  attribute5: projectField(
    'Client document number field',
    'Choose the field that matches the client\'s document numbering convention when that reference matters.',
    'This lets exported data align with the client\'s formal document trail.'
  ),
  attribute6: projectField(
    'Client code field',
    'Choose the short client identifier used in title blocks, packages, or naming rules.',
    'This keeps compact client references available in downstream documentation.'
  ),
  attribute7: projectField(
    'Engineering office field',
    'Choose the engineering office field when the project tracks which office owns the work.',
    'This preserves delivery responsibility context in exported metadata.'
  ),
  attribute8: projectField(
    'Engineering office location field',
    'Choose the location field paired with the engineering office when that distinction matters.',
    'This gives downstream documentation a more specific office reference.'
  ),
  attribute9: projectField(
    'Engineering project number field',
    'Choose the engineering office\'s internal project number if the export must align with consultant tracking.',
    'This helps reconcile the export with engineering deliverable packages.'
  ),
  attribute10: projectField(
    'Engineering area code field',
    'Choose the engineering-side area or package code used to partition work.',
    'This preserves package-level context for downstream deliverables.'
  ),
} as const;

export const mappingConfigurationTooltips = {
  reinitiateWorkflow: tooltip(
    'Revise guided setup',
    'Return to an earlier setup decision when the final mapping reflects the wrong engineering assumptions.',
    'Correct the source decision instead of adding exceptions to the mapping table.',
    'PipeEX rebuilds the downstream mapping context from the revised setup.'
  ),
} as const;

const getWorkflowName = (selectedOption: Option) =>
  selectedOption ?? 'the selected workflow';

export const getStepHelpTooltip = (screen: Screen, selectedOption: Option) => {
  const workflowName = getWorkflowName(selectedOption);

  switch (screen) {
    case 'welcome':
      return tooltip(
        'Setup overview',
        'Review what PipeEX will add or map before it changes the Revit model.',
        'Each later step uses the source decisions made before it.',
        'Confirming the sequence now reduces remapping and model cleanup later.'
      );
    case 'menu':
      return tooltip(
        'Choose workflow',
        `Confirm ${workflowName}, load the shared parameter file, and map the baseline PCF fields.`,
        'These choices determine the remaining setup steps and their source parameters.',
        'PipeEX carries the defaults into connector, bolt, and final mapping rules.'
      );
    case 'connectorBinding':
      return tooltip(
        'Map connectors',
        'Map connector topology, BOM identity, and component metadata to the Revit parameters your families maintain.',
        'These fields define the component data PipeEX reads before final mapping.',
        'PipeEX reuses the selected sources in validation and PCF output.'
      );
    case 'quickFillProperties':
      return tooltip(
        'Complete family data',
        'Use Quick Fill to compare families, fill missing values, and normalize the Revit data used by mapping.',
        'Bulk review exposes inconsistent source values before they become export exceptions.',
        'Values saved here feed final mapping, validation, and PCF output.'
      );
    case 'boltConnection':
      return tooltip(
        'Configure bolts',
        'Choose the PCF bolt structure and map the Revit fields that supply it.',
        'The choice changes validation requirements and where final mapping stores bolt data.',
        'PipeEX either derives standalone BOLT elements from qualifying GASKET elements or uses source BOLT data.'
      );
    case 'additionalCategories':
      return tooltip(
        'Map ISO identifiers',
        'Choose the Revit identifiers that valves, supports, and instruments will carry into ISO output.',
        'Use maintained drawing tags rather than general component descriptions.',
        'The exported identifiers let reviewers trace PCF objects back to drawings and P&IDs.'
      );
    case 'processProperties':
      return tooltip(
        'Map process properties',
        'Map the process conditions and project header fields required in PCF output.',
        'These sources can carry operating limits, compliance data, and document references.',
        'Mapped values give downstream reviewers the engineering context behind each exported system.'
      );
    case 'mappingConfiguration':
      return tooltip(
        'Finalize mapping',
        'Review every Revit source-to-PCF rule by object type and attribute, then save and validate the profile.',
        'This turns the guided setup decisions into explicit export rules.',
        'If a rule is structurally wrong, revise its earlier setup decision instead of patching the table.'
      );
    case 'pcfValidation':
      return tooltip(
        'Resolve validation issues',
        'Trace each finding to its Revit source value or mapping rule, then correct it before export.',
        'Validation checks the combined result of guided setup, Quick Fill, and final mapping.',
        'Resolving findings here prevents incomplete or inconsistent data from reaching PCF consumers.'
      );
    default:
      return null;
  }
};
